import type {
  CreateMeetupInput, MeetupMessage, MeetupParticipant, MeetupParticipantStatus, MeetupStatus, MeetupSummary,
} from "@hanjan-shieo/contracts";

export const CHAT_AFTER_END_MS = 3 * 60 * 60 * 1_000;
export const MESSAGE_RETENTION_MS = 30 * 24 * 60 * 60 * 1_000;
export const MAX_VISIBLE_MESSAGES = 300;

export type MeetupReport = {
  id: string;
  reporterSubjectId: string;
  targetSubjectId: string | null;
  messageId: string | null;
  reason: string;
  detail: string;
  createdAt: string;
  status: "queued" | "reviewing" | "resolved" | "dismissed";
};

export type MeetupState = MeetupSummary & {
  hostSubjectId: string;
  checkInCodeHash: string;
  participants: Record<string, MeetupParticipant>;
  messages: MeetupMessage[];
  reports: MeetupReport[];
  blockedPairs: string[];
  idempotencyKeys: string[];
  rateWindows: Record<string, number[]>;
};

export type MeetupMutation =
  | { ok:true; state:MeetupState; effect:"joined" | "pending" | "approved" | "rejected" | "checked_in" | "left" | "ended" | "cancelled" | "message" | "reported" | "blocked" | "noop" }
  | { ok:false; error:"invalid" | "forbidden" | "not_found" | "conflict" | "closed" | "full" | "rate_limited" | "unsafe" };

const unsafeText = /(?:https?:\/\/|www\.|open\.kakao|카카오톡|오픈채팅|텔레그램|telegram|라인\s?id|instagram|인스타\s?(?:아이디|id)|\b\d{2,3}[-.\s]\d{3,4}[-.\s]\d{4}\b|\b01[016789][-.\s]?\d{3,4}[-.\s]?\d{4}\b|[\w.+-]+@[\w-]+\.[\w.-]+|계좌|송금|입금|조건만남|성매매|원나잇|마약|대마|코인\s?투자|주식\s?리딩|대출|다단계|종교\s?권유|알바\s?모집)/i;
const privateVenue = /(?:자취방|우리\s?집|내\s?집|숙소|호텔\s?방|모텔|펜션|차\s?안|차량\s?안|주차장\s?차량)/i;

export function moderateMeetupText(text: string) {
  if (!text.trim() || text.length > 500) return { allowed:false as const, reason:"length" };
  if (unsafeText.test(text)) return { allowed:false as const, reason:"contact_or_prohibited" };
  return { allowed:true as const };
}

function iso(value: number) { return new Date(value).toISOString(); }
function accepted(status: MeetupParticipantStatus) { return ["host", "accepted", "checked_in"].includes(status); }
function participantsCount(state: MeetupState) { return Object.values(state.participants).filter((participant) => accepted(participant.status)).length; }
function rememberIdempotency(state: MeetupState, key: string) {
  return [...state.idempotencyKeys.filter((value) => value !== key), key].slice(-100);
}
function isReplay(state: MeetupState, key: string) { return state.idempotencyKeys.includes(key); }
function pair(owner: string, target: string) { return `${owner}:${target}`; }

export function createMeetupState(input: {
  id:string;
  data:CreateMeetupInput;
  hostSubjectId:string;
  hostAlias:string;
  checkInCodeHash:string;
  now:string;
}): MeetupState | null {
  const nowMs = Date.parse(input.now);
  const startMs = Date.parse(input.data.startAt);
  const endMs = Date.parse(input.data.plannedEndAt);
  if (![nowMs, startMs, endMs].every(Number.isFinite)) return null;
  if (startMs < nowMs + 30 * 60_000 || startMs > nowMs + 30 * 24 * 60 * 60_000) return null;
  if (endMs < startMs + 60 * 60_000 || endMs > startMs + 6 * 60 * 60_000) return null;
  if (![input.data.title, input.data.venueName, input.data.note || "안전"].every((value) => moderateMeetupText(value).allowed)) return null;
  if (privateVenue.test(`${input.data.venueName}\n${input.data.note}`)) return null;
  const host: MeetupParticipant = { subjectId:input.hostSubjectId, publicAlias:input.hostAlias, status:"host", joinedAt:input.now, checkedInAt:null };
  return {
    ...input.data, id:input.id, hostAlias:input.hostAlias, hostSubjectId:input.hostSubjectId,
    status:"recruiting", participantCount:1, createdAt:input.now, endedAt:null,
    chatClosesAt:iso(endMs + CHAT_AFTER_END_MS), revision:0, checkInCodeHash:input.checkInCodeHash,
    participants:{ [input.hostSubjectId]:host }, messages:[], reports:[], blockedPairs:[], idempotencyKeys:[], rateWindows:{},
  };
}

export function advanceMeetupClock(state: MeetupState, now: string): MeetupState {
  const nowMs = Date.parse(now);
  if (!Number.isFinite(nowMs) || ["cancelled", "archived"].includes(state.status)) return state;
  let next = state;
  const startMs = Date.parse(state.startAt);
  const plannedEndMs = Date.parse(state.plannedEndAt);
  if (["recruiting", "full"].includes(next.status) && nowMs >= startMs) next = { ...next, status:"active", revision:next.revision + 1 };
  if (["recruiting", "full", "active"].includes(next.status) && nowMs >= plannedEndMs) {
    next = { ...next, status:"ended", endedAt:state.plannedEndAt, chatClosesAt:iso(plannedEndMs + CHAT_AFTER_END_MS), revision:next.revision + 1 };
  }
  const closesMs = Date.parse(next.chatClosesAt);
  if (next.status === "ended" && nowMs >= closesMs) next = { ...next, status:"chat_closed", revision:next.revision + 1 };
  if (next.status === "chat_closed" && nowMs >= closesMs + MESSAGE_RETENTION_MS) next = { ...next, status:"archived", messages:[], revision:next.revision + 1 };
  return next;
}

function revisionOkay(state: MeetupState, expectedRevision: number) { return state.revision === expectedRevision; }

export function joinMeetup(stateInput: MeetupState, actor: { subjectId:string; publicAlias:string }, input: { answer:string; expectedRevision:number; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  if (!["recruiting", "full"].includes(state.status)) return { ok:false, error:"closed" };
  const previous = state.participants[actor.subjectId];
  if (previous && !["rejected", "removed", "left"].includes(previous.status)) return { ok:true, state, effect:"noop" };
  if (state.joinMode === "instant" && participantsCount(state) >= state.maxParticipants) return { ok:false, error:"full" };
  if (state.joinMode === "approval" && input.answer && !moderateMeetupText(input.answer).allowed) return { ok:false, error:"unsafe" };
  const participant: MeetupParticipant = {
    subjectId:actor.subjectId, publicAlias:actor.publicAlias,
    status:state.joinMode === "instant" ? "accepted" : "pending", joinedAt:now, checkedInAt:null,
  };
  const participants = { ...state.participants, [actor.subjectId]:participant };
  const count = Object.values(participants).filter((value) => accepted(value.status)).length;
  const next: MeetupState = { ...state, participants, participantCount:count, status:count >= state.maxParticipants ? "full" : "recruiting", revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) };
  return { ok:true, state:next, effect:participant.status === "accepted" ? "joined" : "pending" };
}

export function decideMeetupParticipant(stateInput: MeetupState, actorId: string, input: { subjectId:string; approve:boolean; expectedRevision:number; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  if (actorId !== state.hostSubjectId) return { ok:false, error:"forbidden" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  const participant = state.participants[input.subjectId];
  if (!participant || participant.status !== "pending") return { ok:false, error:"not_found" };
  if (input.approve && participantsCount(state) >= state.maxParticipants) return { ok:false, error:"full" };
  const participants = { ...state.participants, [input.subjectId]:{ ...participant, status:(input.approve ? "accepted" : "rejected") as MeetupParticipantStatus } };
  const count = Object.values(participants).filter((value) => accepted(value.status)).length;
  return { ok:true, state:{ ...state, participants, participantCount:count, status:count >= state.maxParticipants ? "full" : "recruiting", revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) }, effect:input.approve ? "approved" : "rejected" };
}

export function checkInMeetup(stateInput: MeetupState, actorId: string, codeHash: string, input: { expectedRevision:number; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  const participant = state.participants[actorId];
  if (!participant || !accepted(participant.status)) return { ok:false, error:"forbidden" };
  if (state.checkInCodeHash !== codeHash) return { ok:false, error:"invalid" };
  const participants = { ...state.participants, [actorId]:{ ...participant, status:(actorId === state.hostSubjectId ? "host" : "checked_in") as MeetupParticipantStatus, checkedInAt:now } };
  return { ok:true, state:{ ...state, participants, status:"active", revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) }, effect:"checked_in" };
}

export function endMeetup(stateInput: MeetupState, actorId: string, input: { expectedRevision:number; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  if (actorId !== state.hostSubjectId) return { ok:false, error:"forbidden" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  if (["ended", "chat_closed", "archived", "cancelled"].includes(state.status)) return { ok:false, error:"closed" };
  const nowMs = Date.parse(now);
  if (nowMs > Date.parse(state.plannedEndAt) + 2 * 60 * 60_000) return { ok:false, error:"closed" };
  return { ok:true, state:{ ...state, status:"ended", endedAt:now, chatClosesAt:iso(nowMs + CHAT_AFTER_END_MS), revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) }, effect:"ended" };
}

export function leaveMeetup(stateInput: MeetupState, actorId: string, input: { expectedRevision:number; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  if (actorId === state.hostSubjectId) return { ok:false, error:"forbidden" };
  const participant = state.participants[actorId];
  if (!participant || ["left", "rejected", "removed"].includes(participant.status)) return { ok:false, error:"not_found" };
  const participants = { ...state.participants, [actorId]:{ ...participant, status:"left" as const } };
  const count = Object.values(participants).filter((value) => accepted(value.status)).length;
  const status: MeetupStatus = ["ended", "chat_closed", "archived", "cancelled"].includes(state.status)
    ? state.status
    : count >= state.maxParticipants ? "full" : Date.parse(now) >= Date.parse(state.startAt) ? "active" : "recruiting";
  return { ok:true, state:{ ...state, participants, participantCount:count, status, revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) }, effect:"left" };
}

export function cancelMeetup(stateInput: MeetupState, actorId: string, input: { expectedRevision:number; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  if (actorId !== state.hostSubjectId) return { ok:false, error:"forbidden" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  if (["ended", "chat_closed", "archived", "cancelled"].includes(state.status)) return { ok:false, error:"closed" };
  return { ok:true, state:{ ...state, status:"cancelled", endedAt:now, chatClosesAt:now, revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) }, effect:"cancelled" };
}

export function removeMeetupParticipant(stateInput: MeetupState, actorId: string, targetId: string, input: { expectedRevision:number; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  if (actorId !== state.hostSubjectId || actorId === targetId) return { ok:false, error:"forbidden" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  const participant = state.participants[targetId];
  if (!participant || ["left", "rejected", "removed"].includes(participant.status)) return { ok:false, error:"not_found" };
  const participants = { ...state.participants, [targetId]:{ ...participant, status:"removed" as const } };
  const count = Object.values(participants).filter((value) => accepted(value.status)).length;
  const status: MeetupStatus = state.status === "full" && count < state.maxParticipants ? "recruiting" : state.status;
  return { ok:true, state:{ ...state, participants, participantCount:count, status, blockedPairs:[...new Set([...state.blockedPairs, pair(actorId, targetId), pair(targetId, actorId)])], revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) }, effect:"blocked" };
}

export function postMeetupMessage(stateInput: MeetupState, actorId: string, input: { id:string; kind:"text"|"quick"; body:string; expectedRevision:number }, now: string, automatedApproved = true): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (state.messages.some((message) => message.id === input.id)) return { ok:true, state, effect:"noop" };
  if (!revisionOkay(state, input.expectedRevision)) return { ok:false, error:"conflict" };
  const participant = state.participants[actorId];
  if (!participant || !accepted(participant.status)) return { ok:false, error:"forbidden" };
  if (["chat_closed", "archived", "cancelled"].includes(state.status) || Date.parse(now) >= Date.parse(state.chatClosesAt)) return { ok:false, error:"closed" };
  if (!moderateMeetupText(input.body).allowed || !automatedApproved) return { ok:false, error:"unsafe" };
  const recent = (state.rateWindows[actorId] ?? []).filter((stamp) => stamp > Date.parse(now) - 30_000);
  if (recent.length >= 10) return { ok:false, error:"rate_limited" };
  const message: MeetupMessage = { id:input.id, senderSubjectId:actorId, senderAlias:participant.publicAlias, kind:input.kind, body:input.body.trim(), sentAt:now };
  return { ok:true, state:{ ...state, messages:[...state.messages, message].slice(-MAX_VISIBLE_MESSAGES), rateWindows:{ ...state.rateWindows, [actorId]:[...recent, Date.parse(now)] }, revision:state.revision + 1 }, effect:"message" };
}

export function reportMeetup(stateInput: MeetupState, actorId: string, input: { id:string; targetSubjectId?:string; messageId?:string; reason:string; detail:string; idempotencyKey:string }, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (isReplay(state, input.idempotencyKey)) return { ok:true, state, effect:"noop" };
  const participant = state.participants[actorId];
  if (!participant || !accepted(participant.status)) return { ok:false, error:"forbidden" };
  const message = input.messageId ? state.messages.find((value) => value.id === input.messageId) : null;
  const target = input.targetSubjectId ?? message?.senderSubjectId ?? null;
  if (!target || target === actorId || !state.participants[target]) return { ok:false, error:"invalid" };
  const report: MeetupReport = { id:input.id, reporterSubjectId:actorId, targetSubjectId:target, messageId:input.messageId ?? null, reason:input.reason, detail:input.detail, createdAt:now, status:"queued" };
  return { ok:true, state:{ ...state, reports:[...state.reports, report], blockedPairs:[...new Set([...state.blockedPairs, pair(actorId, target)])], revision:state.revision + 1, idempotencyKeys:rememberIdempotency(state, input.idempotencyKey) }, effect:"reported" };
}

export function blockMeetupParticipant(stateInput: MeetupState, actorId: string, targetId: string, now: string): MeetupMutation {
  const state = advanceMeetupClock(stateInput, now);
  if (!acceptedParticipantForBlock(state.participants[actorId]) || !state.participants[targetId] || actorId === targetId) return { ok:false, error:"invalid" };
  if (state.blockedPairs.includes(pair(actorId, targetId))) return { ok:true, state, effect:"noop" };
  return { ok:true, state:{ ...state, blockedPairs:[...new Set([...state.blockedPairs, pair(actorId, targetId)])], revision:state.revision + 1 }, effect:"blocked" };
}

function acceptedParticipantForBlock(participant: MeetupParticipant | undefined) {
  return Boolean(participant && accepted(participant.status));
}

export function visibleMeetupMessages(stateInput: MeetupState, actorId: string, now: string) {
  const state = advanceMeetupClock(stateInput, now);
  const participant = state.participants[actorId];
  if (!participant || !accepted(participant.status)) return [];
  return state.messages.filter((message) => !state.blockedPairs.includes(pair(actorId, message.senderSubjectId)));
}

export function publicMeetupSummary(stateInput: MeetupState, now: string): MeetupSummary {
  const state = advanceMeetupClock(stateInput, now);
  return {
    title:state.title, area:state.area, foodIntent:state.foodIntent,
    startAt:state.startAt, plannedEndAt:state.plannedEndAt,
    maxParticipants:state.maxParticipants, budget:state.budget,
    alcoholStyle:state.alcoholStyle, joinMode:state.joinMode,
    venueName:state.venueName, venueUrl:state.venueUrl, note:state.note,
    id:state.id, hostAlias:state.hostAlias, status:state.status,
    participantCount:state.participantCount, createdAt:state.createdAt,
    endedAt:state.endedAt, chatClosesAt:state.chatClosesAt, revision:state.revision,
  };
}
